import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { runQuery } from '@/features/runs/api';
import { formatDateTime } from '@/lib/format';

export const Route = createFileRoute('/_app/runs/$runId/')({
  component: Overview,
});

function Overview() {
  const { runId } = Route.useParams();
  const { data: run } = useSuspenseQuery(runQuery(runId));

  const rows: [string, string][] = [
    ['Started', formatDateTime(run.startedAt)],
    ['Atlas status', run.atlasStatus ?? '-'],
    ['Stage reached', run.stageReached ? `${run.stageReached.n}. ${run.stageReached.name}` : '-'],
    ['Stages done', `${run.stagesDone} of ${run.stagesTotal}`],
    ['Waiting on', run.waitingOn ?? 'Nothing from staff'],
  ];
  if (run.cancelledAt)
    rows.push([
      'Cancelled',
      `${formatDateTime(run.cancelledAt)}${run.cancelledBy ? ` by ${run.cancelledBy.name}` : ''}`,
    ]);
  if (run.completedAt) rows.push(['Completed', formatDateTime(run.completedAt)]);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Run</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-[8rem_1fr] gap-y-2 text-sm">
            {rows.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="text-muted-foreground">{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Canvas board</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {run.canvasBoardUrl ? (
            <>
              <Button asChild variant="outline">
                <a href={run.canvasBoardUrl} target="_blank" rel="noreferrer noopener">
                  Open Canvas board <ExternalLink />
                </a>
              </Button>
              <p className="text-xs text-muted-foreground">
                View only. If the board does not open for you, staff access to boards is not set up
                yet (pending P1).
              </p>
            </>
          ) : (
            <p className="text-muted-foreground">
              The board is created by Atlas when the run finishes.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
